import React, { useEffect, useState } from 'react';
import { Shield, ShieldCheck, AlertTriangle, Sun, Moon, User } from 'lucide-react';
import { getSafety } from '@/lib/api';

const TIER_META = {
  'very-safe': { label: 'Very safe', color: 'text-[#15291C]', ring: 'rgba(110,231,183,0.6)',  Icon: ShieldCheck },
  safe:        { label: 'Safe',      color: 'text-[#15291C]',    ring: 'rgba(56,189,248,0.5)',   Icon: Shield },
  moderate:    { label: 'Moderate',  color: 'text-[#B85F31]',   ring: 'rgba(251,191,36,0.55)',  Icon: Shield },
  caution:     { label: 'Caution',   color: 'text-[#C66B2C]',  ring: 'rgba(251,146,60,0.6)',   Icon: AlertTriangle },
  'high-risk': { label: 'High-risk', color: 'text-[#B91C2E]',    ring: 'rgba(244,114,128,0.6)',  Icon: AlertTriangle },
};

const LEVEL_DOT = {
  low:     'bg-[#C4FF3D] shadow-[0_0_10px_rgba(110,231,183,0.7)]',
  normal:  'bg-[#C4FF3D]    shadow-[0_0_10px_rgba(56,189,248,0.6)]',
  caution: 'bg-[#F2A47A]   shadow-[0_0_10px_rgba(251,191,36,0.6)]',
  high:    'bg-[#E88A5F]    shadow-[0_0_10px_rgba(244,114,128,0.7)]',
};

export default function SafetyScoreCard({ countryCode, cityName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!countryCode) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    getSafety(countryCode, cityName).then(d => alive && setData(d)).catch(() => alive && setData(null)).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [countryCode, cityName]);

  if (loading) return <div className="puvio-card p-5 gt-skeleton h-32" data-testid="safety-card-skeleton" />;
  if (!data) return null;

  const meta = TIER_META[data.tier] || TIER_META.moderate;
  const Icon = meta.Icon;
  const score = Number(data.score || 0);
  const pct = Math.max(0, Math.min(1, score / 10));

  return (
    <div className="puvio-card p-5" data-testid="safety-card">
      <div className="flex items-start gap-4">
        <div className="relative w-16 h-16 shrink-0">
          <svg viewBox="0 0 64 64" className="w-16 h-16 -rotate-90">
            <circle cx="32" cy="32" r="26" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
            <circle cx="32" cy="32" r="26" fill="none" stroke={meta.ring} strokeWidth="6" strokeLinecap="round"
              strokeDasharray={`${163.36 * pct} 163.36`} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-[15px] font-bold text-[#0F1A13] leading-none" data-testid="safety-score">{score.toFixed(1)}</span>
            <span className="text-[8.5px] uppercase tracking-wider text-[#7A8A80] font-bold">/10</span>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-[10.5px] uppercase tracking-[0.22em] text-[#15291C] flex items-center gap-1.5">
            <Icon className="w-3 h-3" /> Safety score
          </p>
          <p className={`text-sm font-bold mt-0.5 ${meta.color}`} data-testid="safety-tier">{meta.label}</p>
          {data.overlay?.summary && (
            <p className="text-[12px] text-[#4A5A50] mt-1.5 leading-relaxed line-clamp-3" data-testid="safety-summary">
              {data.overlay.summary}
            </p>
          )}
        </div>
      </div>

      {data.overlay && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          {[
            { key: 'day',   label: 'Day',   icon: Sun },
            { key: 'night', label: 'Night', icon: Moon },
            { key: 'women', label: 'Women', icon: User },
          ].map(({ key, label, icon: Ic }) => {
            const lvl = data.overlay[key] || 'normal';
            return (
              <div key={key} className="rounded-2xl bg-[#F2F0E4] border border-[#0F1A13]/06 px-3 py-2.5 flex items-center gap-2" data-testid={`safety-level-${key}`}>
                <Ic className="w-3.5 h-3.5 text-[#7A8A80] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-[#7A8A80] font-semibold">{label}</p>
                  <p className="text-[12px] font-bold text-[#0F1A13] capitalize flex items-center gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${LEVEL_DOT[lvl] || LEVEL_DOT.normal}`} /> {lvl}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {data.overlay?.tips?.length > 0 && (
        <ul className="mt-3 flex flex-col gap-1.5" data-testid="safety-tips">
          {data.overlay.tips.slice(0, 3).map((t, i) => (
            <li key={i} className="text-[12px] text-[#4A5A50] flex gap-2 leading-relaxed">
              <span className="text-[#15291C] mt-0.5">•</span><span>{t}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
